// Lần đến cơ sở: đọc lần đến của khách (theo mã khách POS) và lưu phiếu xác nhận — dùng ở Trừ buổi, Check-in, Hồ sơ 360, Tổng quan thẻ
import { useMemo } from 'react';
import dayjs from 'dayjs';
import usePosStore from '../POS/store';
import useTreatmentStore from '../Treatment/store';
import { collectCustomerData, visitRecordId, visitsOf } from './logic';
import useC360Store from './store';
import { visitSections, visitTitle, type SignParty } from './templates';

export const todayYmd = () => dayjs().format('YYYY-MM-DD');

/** Lần đến của khách theo dữ liệu hiện tại trong các kho (đọc một lần, không theo dõi) */
export function snapshot(posIds: string[], customerKey: string) {
  const p = usePosStore.getState();
  const t = useTreatmentStore.getState();
  const c = useC360Store.getState();
  const d = collectCustomerData(posIds, customerKey, { orders: p.orders, transactions: p.transactions, refunds: p.refunds, cards: t.cards, docs: c.docs, photos: c.photos });
  return visitsOf(d, c.visitRecords.filter(r => posIds.includes(r.posCustomerId)), c.config);
}

/** Lần đến của khách, tự cập nhật khi trừ buổi / thu tiền / ký */
export function useVisits(posIds: string[], customerKey = '') {
  const orders = usePosStore(s => s.orders);
  const transactions = usePosStore(s => s.transactions);
  const refunds = usePosStore(s => s.refunds);
  const cards = useTreatmentStore(s => s.cards);
  const docs = useC360Store(s => s.docs);
  const photos = useC360Store(s => s.photos);
  const records = useC360Store(s => s.visitRecords);
  const cfg = useC360Store(s => s.config);
  const idKey = posIds.join(',');
  return useMemo(() => {
    const d = collectCustomerData(posIds, customerKey, { orders, transactions, refunds, cards, docs, photos });
    return visitsOf(d, records.filter(r => posIds.includes(r.posCustomerId)), cfg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, customerKey, orders, transactions, refunds, cards, docs, photos, records, cfg]);
}

export interface SignVisitInput {
  posIds: string[];
  customerKey?: string;
  date: string;
  /** Mục khách ký (mặc định: mọi mục chưa ký của lần đến) */
  keys?: string[];
  party: SignParty;
  signature: string;
  witness: string;
  branchId: string;
  note?: string;
  rating?: number;
  ratingNote?: string;
}

/** Lưu phiếu "Xác nhận thực hiện dịch vụ" cho một lần đến (đọc dữ liệu mới nhất ngay lúc ký) */
export async function signVisit(input: SignVisitInput) {
  const v = snapshot(input.posIds, input.customerKey || '').find(x => x.date === input.date);
  if (!v) return null;
  const keys = input.keys?.length ? input.keys : v.unsigned.map(i => i.key);
  if (!keys.length) return null;
  const store = useC360Store.getState();
  const posCustomerId = v.items.find(i => keys.includes(i.key))?.posCustomerId || v.posCustomerIds[0];
  const doc = await store.sign({
    kind: 'visit', title: visitTitle(v.date), customerKey: input.customerKey, posCustomerId, visitDate: v.date, coveredKeys: keys,
    sections: visitSections(input.party, v, keys, { note: input.note, rating: input.rating, ratingNote: input.ratingNote }),
    signerName: input.party.name, signerPhone: input.party.phone, witness: input.witness, branchId: input.branchId, signature: input.signature,
  });
  store.upsertVisitRecord(visitRecordId(posCustomerId, v.date), {
    posCustomerId, date: v.date, deferred: undefined,
    ...(input.rating ? { rating: input.rating, ratingNote: input.ratingNote } : {}),
  });
  return doc;
}
